import React, { useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Trophy, AlertCircle, Layers, RefreshCcw } from 'lucide-react';

/**
 * CLO Manager Scorecard — Phase C4 (read-only descriptive ranking).
 *
 * Aggregates deal-level OC/IC, WARF migration, CCC bucket, lifecycle phase
 * mix across every CLO manager active in the tenant. Output is a ranked list
 * with weighted metrics and a per-deal drill-in. The panel does NOT
 * recommend or score managers — it surfaces empirical performance for the
 * allocator to interpret.
 */

interface DealLine {
  dealId: number;
  dealName: string;
  status: string;
  par: number;
  warf?: number;
  was?: number;
  wal?: number;
  ocCushion?: number;
  icCushion?: number;
  cccPct?: number;
  defaultedRatio?: number;
}

interface ManagerScorecard {
  managerName: string;
  dealCount: number;
  totalCollateralPar: number;
  totalDefaultedPar: number;
  defaultedPct: number;
  currentWarf?: number;
  priorWarf?: number;
  warfMigration?: number;
  weightedWas?: number;
  weightedWal?: number;
  weightedDiversity?: number;
  weightedCccPct?: number;
  weightedOcCushion?: number;
  weightedIcCushion?: number;
  phaseMix: Record<string, number>;
  deals: DealLine[];
  asOf: string;
}

const Wrap = styled.div`
  padding: 1.25rem 0 1rem;
  color: #cbd5e1;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const TitleBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;

  svg {
    width: 18px;
    height: 18px;
    color: #fbbf24;
  }
`;

const Title = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  color: #e2e8f0;
`;

const Subtitle = styled.div`
  font-size: 0.78rem;
  color: #94a3b8;
  margin-top: 2px;
`;

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.85rem;
  background: rgba(15, 23, 42, 0.55);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  color: #cbd5e1;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  cursor: pointer;

  svg { width: 14px; height: 14px; }

  &:hover {
    border-color: #fbbf24;
    color: white;
  }
`;

const ScorecardCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(51, 65, 85, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border-left: 4px solid #fbbf24;
`;

const ScorecardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
`;

const ManagerName = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;

  .rank {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, rgba(234, 179, 8, 0.18), rgba(234, 179, 8, 0.06));
    border: 1px solid rgba(234, 179, 8, 0.4);
    color: #fbbf24;
    border-radius: 8px;
    font-weight: 700;
    font-family: 'JetBrains Mono', monospace;
    font-size: 0.95rem;
  }
  .name {
    color: white;
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: -0.01em;
  }
  .deal-count {
    color: #94a3b8;
    font-size: 0.78rem;
    margin-left: 0.5rem;
  }
`;

const ParStat = styled.div`
  text-align: right;

  .label {
    font-size: 0.7rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
    font-weight: 600;
    margin-bottom: 4px;
  }
  .value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 1.3rem;
    color: white;
    font-weight: 600;
  }
  .sub {
    font-size: 0.7rem;
    color: #94a3b8;
    margin-top: 2px;
  }
`;

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const MetricTile = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 0.75rem 0.85rem;

  .label {
    font-size: 0.66rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #64748b;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .value {
    font-family: 'JetBrains Mono', monospace;
    font-size: 1.05rem;
    color: #e2e8f0;
    font-weight: 600;
  }
  .delta {
    font-size: 0.7rem;
    margin-top: 3px;
  }
`;

const PhaseMix = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const PhaseChip = styled.span<{ $phase: string }>`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 0.68rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 700;
  background: ${(p) => phaseColor(p.$phase).bg};
  color: ${(p) => phaseColor(p.$phase).fg};
  border: 1px solid ${(p) => phaseColor(p.$phase).border};

  .count {
    margin-left: 6px;
    color: rgba(255, 255, 255, 0.7);
    font-family: 'JetBrains Mono', monospace;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 0.55rem 0.75rem;
  font-size: 0.66rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  font-weight: 600;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(15, 23, 42, 0.4);
`;

const Td = styled.td`
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  color: #cbd5e1;
`;

const TdMono = styled(Td)`
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.78rem;
`;

const TdRight = styled(TdMono)`
  text-align: right;
`;

const StatusBadge = styled.span<{ $status: string }>`
  display: inline-block;
  padding: 1px 8px;
  border-radius: 4px;
  font-size: 0.62rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-weight: 700;
  background: ${(p) => statusColor(p.$status).bg};
  color: ${(p) => statusColor(p.$status).fg};
  border: 1px solid ${(p) => statusColor(p.$status).border};
`;

const EmptyState = styled.div`
  padding: 3rem 1.5rem;
  text-align: center;
  color: #94a3b8;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 8px;

  svg {
    width: 28px;
    height: 28px;
    color: #64748b;
    margin-bottom: 0.75rem;
  }
`;

const Disclaimer = styled.div`
  font-size: 0.72rem;
  color: #64748b;
  margin: 0 0 1rem;
  font-style: italic;
`;

function phaseColor(phase: string): { bg: string; fg: string; border: string } {
  const upper = (phase || '').toUpperCase();
  if (upper.includes('REINVEST'))
    return { bg: 'rgba(16, 185, 129, 0.12)', fg: '#86efac', border: 'rgba(16, 185, 129, 0.3)' };
  if (upper.includes('AMORT'))
    return { bg: 'rgba(59, 130, 246, 0.12)', fg: '#93c5fd', border: 'rgba(59, 130, 246, 0.3)' };
  if (upper.includes('RAMP') || upper.includes('WAREHOUSE'))
    return { bg: 'rgba(168, 85, 247, 0.12)', fg: '#d8b4fe', border: 'rgba(168, 85, 247, 0.3)' };
  if (upper.includes('CALLED') || upper.includes('REFI') || upper.includes('RESET'))
    return { bg: 'rgba(234, 179, 8, 0.12)', fg: '#fde68a', border: 'rgba(234, 179, 8, 0.3)' };
  return { bg: 'rgba(148, 163, 184, 0.12)', fg: '#cbd5e1', border: 'rgba(148, 163, 184, 0.3)' };
}

function statusColor(status: string): { bg: string; fg: string; border: string } {
  const upper = (status || '').toUpperCase();
  if (upper === 'REINVESTMENT')
    return { bg: 'rgba(16, 185, 129, 0.14)', fg: '#86efac', border: 'rgba(16, 185, 129, 0.3)' };
  if (upper === 'AMORTIZING')
    return { bg: 'rgba(59, 130, 246, 0.14)', fg: '#93c5fd', border: 'rgba(59, 130, 246, 0.3)' };
  if (upper === 'CALLED' || upper === 'TERMINATED')
    return { bg: 'rgba(239, 68, 68, 0.14)', fg: '#fca5a5', border: 'rgba(239, 68, 68, 0.3)' };
  return { bg: 'rgba(148, 163, 184, 0.12)', fg: '#cbd5e1', border: 'rgba(148, 163, 184, 0.3)' };
}

const fmtMoney = (v?: number) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1_000_000_000) return `$${(v / 1_000_000_000).toFixed(2)}B`;
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toLocaleString()}`;
};

const fmtPct = (v?: number, decimals = 2) =>
  v == null || isNaN(v) ? '—' : `${v.toFixed(decimals)}%`;

const fmtNum = (v?: number, decimals = 0) =>
  v == null || isNaN(v) ? '—' : v.toFixed(decimals);

const CLOManagerScorecardPanel: React.FC = () => {
  const [scorecards, setScorecards] = useState<ManagerScorecard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchScorecards = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get<ManagerScorecard[]>('/api/clo/managers/scorecard');
      setScorecards(Array.isArray(r.data) ? r.data : []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load CLO manager scorecards.');
      setScorecards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchScorecards();
  }, []);

  return (
    <Wrap>
      <HeaderRow>
        <TitleBlock>
          <Trophy />
          <div>
            <Title>CLO Manager Scorecard</Title>
            <Subtitle>
              Cross-deal aggregation of OC/IC, WARF migration, CCC bucket, and lifecycle phase mix
            </Subtitle>
          </div>
        </TitleBlock>
        <RefreshBtn onClick={fetchScorecards}>
          <RefreshCcw />
          Refresh
        </RefreshBtn>
      </HeaderRow>

      <Disclaimer>
        Read-only descriptive ranking. AxiomFlow does not score or recommend managers — interpretation is the allocator's responsibility (IBOR scope).
      </Disclaimer>

      {loading && <EmptyState>Loading manager scorecards…</EmptyState>}
      {!loading && error && (
        <EmptyState>
          <AlertCircle />
          <div>{error}</div>
        </EmptyState>
      )}
      {!loading && !error && scorecards.length === 0 && (
        <EmptyState>
          <Layers />
          <div>No CLO managers visible in this tenant.</div>
        </EmptyState>
      )}

      {!loading && !error && scorecards.map((s, idx) => {
        const warfMigration = s.warfMigration ?? 0;
        const migrationDeteriorating = warfMigration > 0;
        return (
          <ScorecardCard
            key={s.managerName}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.04 }}
          >
            <ScorecardHead>
              <ManagerName>
                <span className="rank">#{idx + 1}</span>
                <div>
                  <div className="name">{s.managerName}</div>
                  <div className="deal-count">{s.dealCount} deal{s.dealCount === 1 ? '' : 's'} as of {s.asOf}</div>
                </div>
              </ManagerName>
              <ParStat>
                <div className="label">Total Collateral Par</div>
                <div className="value">{fmtMoney(s.totalCollateralPar)}</div>
                <div className="sub">
                  Defaulted: {fmtMoney(s.totalDefaultedPar)} ({fmtPct(s.defaultedPct, 2)})
                </div>
              </ParStat>
            </ScorecardHead>

            <MetricsRow>
              <MetricTile>
                <div className="label">Current WARF</div>
                <div className="value">{fmtNum(s.currentWarf, 0)}</div>
                <div className="delta" style={{ color: migrationDeteriorating ? '#fca5a5' : '#86efac' }}>
                  {warfMigration === 0 ? '— flat' :
                    `${migrationDeteriorating ? '+' : ''}${fmtNum(warfMigration, 0)} vs prior`}
                </div>
              </MetricTile>
              <MetricTile>
                <div className="label">Wtd OC Cushion</div>
                <div className="value">{fmtPct(s.weightedOcCushion, 2)}</div>
              </MetricTile>
              <MetricTile>
                <div className="label">Wtd IC Cushion</div>
                <div className="value">{fmtPct(s.weightedIcCushion, 2)}</div>
              </MetricTile>
              <MetricTile>
                <div className="label">Wtd CCC Bucket</div>
                <div className="value">{fmtPct(s.weightedCccPct, 2)}</div>
              </MetricTile>
              <MetricTile>
                <div className="label">Wtd WAS</div>
                <div className="value">{s.weightedWas != null ? `${fmtNum(s.weightedWas, 0)}bp` : '—'}</div>
              </MetricTile>
              <MetricTile>
                <div className="label">Wtd WAL</div>
                <div className="value">{s.weightedWal != null ? `${fmtNum(s.weightedWal, 1)}y` : '—'}</div>
              </MetricTile>
              <MetricTile>
                <div className="label">Wtd Diversity</div>
                <div className="value">{fmtNum(s.weightedDiversity, 0)}</div>
              </MetricTile>
            </MetricsRow>

            {s.phaseMix && Object.keys(s.phaseMix).length > 0 && (
              <PhaseMix>
                {Object.entries(s.phaseMix).map(([phase, count]) => (
                  <PhaseChip key={phase} $phase={phase}>
                    {phase}
                    <span className="count">{count}</span>
                  </PhaseChip>
                ))}
              </PhaseMix>
            )}

            {s.deals && s.deals.length > 0 && (
              <Table>
                <thead>
                  <tr>
                    <Th>Deal</Th>
                    <Th>Status</Th>
                    <Th style={{ textAlign: 'right' }}>Par</Th>
                    <Th style={{ textAlign: 'right' }}>WARF</Th>
                    <Th style={{ textAlign: 'right' }}>WAS</Th>
                    <Th style={{ textAlign: 'right' }}>WAL</Th>
                    <Th style={{ textAlign: 'right' }}>OC Cushion</Th>
                    <Th style={{ textAlign: 'right' }}>IC Cushion</Th>
                    <Th style={{ textAlign: 'right' }}>CCC %</Th>
                    <Th style={{ textAlign: 'right' }}>Defaulted %</Th>
                  </tr>
                </thead>
                <tbody>
                  {s.deals.map((d) => (
                    <tr key={d.dealId}>
                      <Td>{d.dealName}</Td>
                      <Td><StatusBadge $status={d.status}>{d.status}</StatusBadge></Td>
                      <TdRight>{fmtMoney(d.par)}</TdRight>
                      <TdRight>{fmtNum(d.warf, 0)}</TdRight>
                      <TdRight>{d.was != null ? `${fmtNum(d.was, 0)}bp` : '—'}</TdRight>
                      <TdRight>{d.wal != null ? `${fmtNum(d.wal, 1)}y` : '—'}</TdRight>
                      <TdRight>{fmtPct(d.ocCushion, 2)}</TdRight>
                      <TdRight>{fmtPct(d.icCushion, 2)}</TdRight>
                      <TdRight>{fmtPct(d.cccPct, 2)}</TdRight>
                      <TdRight>{d.defaultedRatio != null ? fmtPct(d.defaultedRatio * 100, 2) : '—'}</TdRight>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </ScorecardCard>
        );
      })}
    </Wrap>
  );
};

export default CLOManagerScorecardPanel;
